import { Controller } from "@hotwired/stimulus";

/**
 * Nested Sortable Controller
 *
 * Supports drag-and-drop reordering of items at the same level:
 * - Root-level items can be reordered among each other
 * - Child items within a container can be reordered among siblings
 * - Items cannot be dragged between levels; use "Add child" buttons instead
 *
 * On page load, items with a nu_parent_id are moved into their parent's
 * children container so the visual hierarchy matches the data.
 */
export default class extends Controller {
  static targets = [
    "item", // each sortable row/item
    "handle", // drag handle inside an item
    "positionInput", // position input inside an item
    "parentInput", // parent_id input inside an item
    "destroyInput", // _destroy input inside an item
    "links", // container with add links to act as anchor for insertBefore
    "childrenContainer", // container for child items within a parent
  ];

  connect() {
    this.dragged = null;
    this.isConnected = true;
    this.initialized = false;

    this.boundTurboLoadHandler = this.handleTurboLoad.bind(this);
    document.addEventListener("turbo:load", this.boundTurboLoadHandler);

    this.tryInit();
  }

  handleTurboLoad() {
    if (this.isConnected && !this.initialized) {
      this.tryInit();
    }
  }

  tryInit() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      if (this.itemTargets.length === 0) {
        requestAnimationFrame(() => this.tryInit());
        return;
      }

      this.performInit();
    });
  }

  performInit() {
    if (this.initialized || !this.isConnected) return;
    this.initialized = true;

    this.linksEl = this.hasLinksTarget ? this.linksTarget : null;

    // Move children into their parent containers based on nu_parent_id values
    this.organizeChildrenByParent();

    this.initItems();
    this.updatePositions();

    this.observerRAF = null;
    this.observer = new MutationObserver(() => {
      // Debounce: batch rapid mutations into a single callback per frame
      if (this.observerRAF) cancelAnimationFrame(this.observerRAF);
      this.observerRAF = requestAnimationFrame(() => {
        this.observerRAF = null;
        this.linksEl = this.hasLinksTarget ? this.linksTarget : null;
        this.initItems();
        this.updatePositions();
      });
    });
    this.startObserver();
  }

  // On page load, move items with nu_parent_id into their parent's children container.
  // Rails renders all sections flat; this visually nests existing children.
  organizeChildrenByParent() {
    this.itemTargets.forEach((item) => {
      const parentInput = item.querySelector(
        this.targetSelector("parentInput"),
      );
      const parentId = parentInput?.value;

      if (parentId) {
        const parentContainer = this.element.querySelector(
          `[data-parent-section-id="${parentId}"]`,
        );
        if (parentContainer && item.parentElement !== parentContainer) {
          // Save select values before the DOM move. Moving a subtree
          // triggers Stimulus disconnect/reconnect on controllers within
          // it. The basic-select controller destroys and recreates its
          // TomSelect instance during this cycle, which loses the
          // selected value. We restore values on the native <select>
          // after the move so the fresh TomSelect picks them up.
          const savedSelects = this.saveSelectValues(item);

          // Detach Trix editors before the move to prevent toolbar loss.
          const detachedTrix = this.detachTrixEditors(item);

          const addLink = parentContainer.querySelector(
            ":scope > .children-add-link",
          );
          if (addLink) {
            parentContainer.insertBefore(item, addLink);
          } else {
            parentContainer.appendChild(item);
          }

          this.reattachTrixEditors(detachedTrix);
          this.restoreSelectValues(savedSelects);

          item.classList.add("nested-child");
        }
      }
    });
  }

  startObserver() {
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  // --- Add child ---

  /**
   * Adds a new child item inside a parent's children container.
   * Stamps a new record from the page-level template, sets its nu_parent_id,
   * applies child styling, and inserts it into the container.
   */
  addChild(event) {
    event.preventDefault();

    const item = event.target.closest(this.targetSelector("item"));
    if (!item) return;

    const pid = this.parentId(item);
    if (!pid) return;

    // Find the page-level template (used by forms--nested-attrs)
    const template = this.element.querySelector(
      '[data-forms--nested-attrs-target="template"]',
    );
    if (!template) return;

    const newIndex = new Date().getTime();
    const content = template.innerHTML.replace(/NEW_RECORD/g, newIndex);

    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;
    const newItem = wrapper.firstElementChild;
    if (!newItem) return;

    // Set the parent_id on the new child
    const parentInput = newItem.querySelector(
      this.targetSelector("parentInput"),
    );
    if (parentInput) parentInput.value = pid;

    // Add child visual styling
    newItem.classList.add("nested-child");

    // Add child badge after the label text in the accordion header
    const header = newItem.querySelector(".accordion-button");
    if (header) {
      const badge = document.createElement("span");
      badge.className = "badge bg-secondary ms-2";
      badge.textContent = "Child";
      // Insert after the first text node or element
      header.appendChild(badge);
    }

    // Hide the children container on the new child (single-level nesting)
    const childContainer = newItem.querySelector(".children-container");
    if (childContainer) childContainer.classList.add("d-none");

    // Disable accordion parent behaviour so children can be opened independently
    const collapse = newItem.querySelector(".accordion-collapse");
    if (collapse) collapse.removeAttribute("data-bs-parent");

    // Insert into the parent's children container, before the add button
    const container = item.querySelector(
      this.targetSelector("childrenContainer"),
    );
    if (!container) return;

    const addButton = container.querySelector(":scope > .children-add-link");
    if (addButton) {
      container.insertBefore(newItem, addButton);
    } else {
      container.appendChild(newItem);
    }
  }

  /**
   * Returns the persisted parent ID for the given item.
   * Children can only be added to persisted items, so the
   * data-parent-section-id attribute is always set from the record's id.
   */
  parentId(item) {
    const container = item.querySelector(
      this.targetSelector("childrenContainer"),
    );
    return container?.dataset.parentSectionId || null;
  }

  // --- DOM preservation helpers ---

  saveSelectValues(root) {
    const entries = [];
    root.querySelectorAll("select").forEach((select) => {
      const value = select.tomselect
        ? select.tomselect.getValue()
        : select.value;
      entries.push({ select, value });
    });
    return entries;
  }

  restoreSelectValues(entries) {
    entries.forEach(({ select, value }) => {
      select.value = value;
    });
  }

  detachTrixEditors(root) {
    const entries = [];
    root.querySelectorAll("trix-editor").forEach((editor) => {
      const parent = editor.parentNode;
      if (!parent) return;

      const toolbar = editor.previousElementSibling;
      const isToolbar =
        toolbar && toolbar.tagName.toLowerCase() === "trix-toolbar";

      const placeholder = document.createComment("trix-placeholder");
      parent.insertBefore(placeholder, editor);

      if (isToolbar) toolbar.remove();
      editor.remove();

      entries.push({
        editor,
        toolbar: isToolbar ? toolbar : null,
        placeholder,
      });
    });
    return entries;
  }

  reattachTrixEditors(entries) {
    entries.forEach(({ editor, toolbar, placeholder }) => {
      const parent = placeholder.parentNode;
      if (!parent) return;

      if (toolbar) parent.insertBefore(toolbar, placeholder);
      parent.insertBefore(editor, placeholder);
      placeholder.remove();
    });
  }

  // --- Drag and drop (same-level reordering only) ---

  initItems() {
    this.itemTargets.forEach((item) => {
      item.removeAttribute("draggable");

      const handle = item.querySelector(this.targetSelector("handle"));
      if (!handle) return;
      if (handle.dataset.nestedSortableInitialized === "true") return;

      handle.setAttribute("draggable", "true");
      handle.addEventListener("dragstart", this.onDragStart);
      handle.addEventListener("dragend", this.onDragEnd);
      handle.dataset.nestedSortableInitialized = "true";
    });

    if (!this.element.dataset.nestedSortableContainerInitialized) {
      this.element.addEventListener("dragover", this.onDragOver);
      this.element.addEventListener("drop", this.onDrop);
      this.element.dataset.nestedSortableContainerInitialized = "true";
    }

    // Allow sibling reordering within children containers
    this.childrenContainerTargets.forEach((container) => {
      if (!container.dataset.nestedSortableChildInitialized) {
        container.addEventListener("dragover", this.onChildContainerDragOver);
        container.addEventListener("drop", this.onDrop);
        container.dataset.nestedSortableChildInitialized = "true";
      }
    });
  }

  get rootItems() {
    return this.itemTargets.filter((item) => {
      const isInsideChildContainer = item.parentElement?.matches(
        this.targetSelector("childrenContainer"),
      );
      return !isInsideChildContainer;
    });
  }

  dragContext(item) {
    const parentContainer = item.parentElement?.closest(
      this.targetSelector("childrenContainer"),
    );
    return parentContainer || this.element;
  }

  onDragStart = (e) => {
    const handle = e.currentTarget;
    const item = handle.closest(this.targetSelector("item"));
    if (!item) return;

    // Pause the observer to avoid feedback loops during drag
    if (this.observerRAF) cancelAnimationFrame(this.observerRAF);
    this.observer.disconnect();

    this.dragged = item;
    this.dragContainer = this.dragContext(item);
    this.dragged.classList.add("dragging");
    this.dragRAF = null;

    // Cache sibling items for the drag context so we don't recompute on every dragover
    if (this.dragContainer === this.element) {
      this.cachedDragSiblings = this.rootItems;
    } else {
      this.cachedDragSiblings = Array.from(
        this.dragContainer.querySelectorAll(
          `:scope > ${this.targetSelector("item")}`,
        ),
      );
    }

    const collapse = item.querySelector(".accordion-collapse");
    if (collapse) collapse.classList.remove("show");

    // Collapse children container to reduce DOM subtree size during drag
    const childrenContainer = item.querySelector(
      this.targetSelector("childrenContainer"),
    );
    if (childrenContainer) {
      this.draggedChildrenContainer = childrenContainer;
      childrenContainer.style.display = "none";
    }

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  onDragEnd = (e) => {
    if (this.dragRAF) {
      cancelAnimationFrame(this.dragRAF);
      this.dragRAF = null;
    }

    if (this.dragged) {
      // Restore children container visibility
      if (this.draggedChildrenContainer) {
        this.draggedChildrenContainer.style.display = "";
        this.draggedChildrenContainer = null;
      }

      this.dragged.classList.remove("dragging");
      this.dragged = null;
      this.dragContainer = null;
      this.cachedDragSiblings = null;
    }
    this.updatePositions();

    // Reconnect the observer now that drag is complete
    this.startObserver();
  };

  onDragOver = (e) => {
    if (!this.dragged) return;

    // Don't handle if we're over a children container
    const overChildContainer = e.target.closest(
      this.targetSelector("childrenContainer"),
    );
    if (overChildContainer) return;

    // Only root items can be reordered at root level
    if (this.dragContainer !== this.element) return;

    e.preventDefault();

    // Throttle DOM moves to once per animation frame
    if (this.dragRAF) return;
    const y = e.clientY;
    this.dragRAF = requestAnimationFrame(() => {
      this.dragRAF = null;
      if (!this.dragged) return;

      const afterElement = this.getDragAfterElement(
        this.element,
        this.cachedDragSiblings,
        y,
      );

      if (afterElement == null) {
        const anchor = this.linksEl;
        if (anchor) {
          if (anchor.previousElementSibling !== this.dragged) {
            this.element.insertBefore(this.dragged, anchor);
          }
        } else if (this.element.lastElementChild !== this.dragged) {
          this.element.appendChild(this.dragged);
        }
      } else if (afterElement.previousElementSibling !== this.dragged) {
        this.element.insertBefore(this.dragged, afterElement);
      }
    });
  };

  // Handle reordering of children within a container
  onChildContainerDragOver = (e) => {
    if (!this.dragged) return;

    const container = e.currentTarget;

    // Only allow reordering if dragged item belongs to this container
    if (this.dragContainer !== container) return;

    e.preventDefault();
    e.stopPropagation();

    // Throttle DOM moves to once per animation frame
    if (this.dragRAF) return;
    const y = e.clientY;
    this.dragRAF = requestAnimationFrame(() => {
      this.dragRAF = null;
      if (!this.dragged) return;

      const afterElement = this.getDragAfterElement(
        container,
        this.cachedDragSiblings,
        y,
      );

      if (afterElement == null) {
        const addButton = container.querySelector(
          ":scope > .children-add-link",
        );
        if (addButton) {
          container.insertBefore(this.dragged, addButton);
        } else {
          container.appendChild(this.dragged);
        }
      } else if (afterElement.previousElementSibling !== this.dragged) {
        container.insertBefore(this.dragged, afterElement);
      }
    });
  };

  onDrop = (e) => {
    e.preventDefault();
    this.updatePositions();
  };

  getDragAfterElement(container, candidates, y) {
    const filtered = candidates.filter(
      (el) => el !== this.dragged && !this.isDestroyed(el),
    );

    let closest = { offset: Number.NEGATIVE_INFINITY, element: null };
    filtered.forEach((child) => {
      const box = child.getBoundingClientRect();
      const offset = y - (box.top + box.height / 2);
      if (offset < 0 && offset > closest.offset) {
        closest = { offset, element: child };
      }
    });
    return closest.element;
  }

  updatePositions() {
    let rootIndex = 1;
    this.rootItems.forEach((item) => {
      if (this.isDestroyed(item)) return;

      const positionInput = item.querySelector(
        this.targetSelector("positionInput"),
      );
      if (positionInput) {
        positionInput.value = rootIndex++;
      }
    });

    this.childrenContainerTargets.forEach((container) => {
      let childIndex = 1;
      const children = container.querySelectorAll(
        `:scope > ${this.targetSelector("item")}`,
      );
      children.forEach((item) => {
        if (this.isDestroyed(item)) return;

        const positionInput = item.querySelector(
          this.targetSelector("positionInput"),
        );
        if (positionInput) {
          positionInput.value = childIndex++;
        }
      });
    });
  }

  // --- Common helpers ---

  isDestroyed(item) {
    const destroyInput = item.querySelector(
      this.targetSelector("destroyInput"),
    );
    const isDestroyed = destroyInput && destroyInput.value === "1";
    const isHidden = item.style && item.style.display === "none";
    return isDestroyed || isHidden;
  }

  targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`;
  }

  disconnect() {
    this.isConnected = false;
    if (this.boundTurboLoadHandler) {
      document.removeEventListener("turbo:load", this.boundTurboLoadHandler);
    }
    if (this.dragRAF) cancelAnimationFrame(this.dragRAF);
    if (this.observerRAF) cancelAnimationFrame(this.observerRAF);
    if (this.observer) this.observer.disconnect();
  }
}
